import type { PerformanceClinic } from '@/entities/performance/api/performance.api';
import {
  useClinicTrend,
  useMonthlySummary,
} from '@/entities/performance/hooks/usePerformanceQueries';
import {
  monthOf,
  type TrendPoint,
} from '@/entities/performance/lib/performance';
import { Badge } from '@/shared/components/ui/badge';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { formatWon } from '@/shared/utils/format';
import { useMemo, type ReactNode } from 'react';
import {
  DeltaBadge,
  PERF_BADGE,
  PerfBadge,
  TrendLegend,
} from './PerformanceParts';
import { TrendChart } from './TrendChart';

const StatRow = ({
  label,
  amount,
  delta,
}: {
  label: string;
  amount: number | null | undefined;
  delta: number | null | undefined;
}) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-[11.5px] leading-[17px] whitespace-nowrap text-[#8d8d8d]">
      {label}
    </span>
    <span className="flex items-center gap-1.5">
      <span className="text-[13px] leading-5 font-medium whitespace-nowrap text-ink-2">
        {formatWon(amount)}
      </span>
      <DeltaBadge value={delta} />
    </span>
  </div>
);

const StatBox = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className="flex flex-1 flex-col gap-1.5 rounded-lg bg-[#fafafa] px-4 py-[13px]">
    <p className="mb-0.5 text-xs leading-[18px] font-bold text-ink">{title}</p>
    {children}
  </div>
);

const AmountBox = ({
  kind,
  target,
  total,
  ratio,
}: {
  kind: 'prescription' | 'settlement';
  target: number | null | undefined;
  total: number | null | undefined;
  ratio?: number | null;
}) => {
  const isRx = kind === 'prescription';
  return (
    <div className="flex flex-col rounded-lg bg-[#fafafa] px-[18px] py-3.5">
      <div className="flex h-[22px] items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <PerfBadge className={isRx ? PERF_BADGE.info : PERF_BADGE.settled}>
            {isRx ? '처방' : '정산'}
          </PerfBadge>
          <span className="text-xs leading-[18px] text-[#8d8d8d]">
            정산 대상 금액
          </span>
        </span>
        <span className="text-[13.5px] leading-5 font-medium text-ink-3">
          {formatWon(target)}
        </span>
      </div>
      <div className="mt-2 flex h-7 items-center justify-between gap-2">
        <span className="text-[12.5px] leading-[19px] font-medium text-ink-2">
          {isRx ? '예상 금액' : '확정 금액'}
        </span>
        <span className="text-lg leading-[30px] font-bold text-ink lg:text-xl">
          {formatWon(total)}
        </span>
      </div>
      {ratio !== undefined && (
        <div className="mt-2.5 flex items-center justify-between">
          <span className="text-[11.5px] leading-[17px] text-[#8d8d8d]">
            정산 비율
          </span>
          <span className="flex items-center gap-2">
            <span className="h-1.5 w-[120px] overflow-hidden rounded-[3px] bg-[#e8e8e8]">
              <span
                className="block h-full rounded-[3px] bg-success"
                style={{ width: `${Math.min(100, Math.max(0, ratio ?? 0))}%` }}
              />
            </span>
            <span className="text-[11.5px] leading-[17px] font-bold text-success">
              {ratio === null ? '—' : `${Math.round(ratio)}%`}
            </span>
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * M-09 우측 상단 — 병의원 이름 · 월별 추세 · 직전 5개월 평균 · 전 월 대비 · 이번 달 금액
 * 추세 · 비교는 GET /trend, 이번 달 금액과 정산 비율은 GET /summary?clinicId= 값을 그대로 쓴다.
 * 월 요약은 검수 중 추정치를 포함하고 추이는 포함하지 않는다.
 */
export const ClinicSummary = ({
  ym,
  clinicId,
  clinic,
  clinicLoading,
}: {
  ym: string;
  clinicId: number;
  /** 좌측 목록에서 찾은 병의원 — 목록에 없는 병의원(URL로 진입)이면 이름 줄을 비운다 */
  clinic?: PerformanceClinic;
  clinicLoading: boolean;
}) => {
  const year = Number(ym.slice(0, 4));
  const trend = useClinicTrend(ym, clinicId);
  const monthly = useMonthlySummary(ym, clinicId);
  const m = monthly.data;
  const t = trend.data;
  const points = useMemo<TrendPoint[]>(
    () =>
      (t?.months ?? []).map((p) => ({
        ym: p.settlementYearMonth,
        estimated: p.estimatedAmount,
        confirmed: p.confirmedAmount > 0 ? p.confirmedAmount : null,
      })),
    [t],
  );

  return (
    <>
      {clinic ? (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg leading-[30px] font-bold break-keep text-ink lg:text-xl">
              {clinic.clinicName}
            </h2>
            {clinic.clinicTypeLabel && (
              <Badge
                tone={
                  clinic.clinicTypeLabel === '병원급' ? 'success' : 'primary'
                }
              >
                {clinic.clinicTypeLabel}
              </Badge>
            )}
          </div>
          {(clinic.clinicTypeCodeName || clinic.address) && (
            <p className="text-xs leading-[18px] text-ink-5">
              {[clinic.clinicTypeCodeName, clinic.address]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
        </div>
      ) : (
        clinicLoading && <Skeleton className="h-[30px] w-60" />
      )}
      <section className="mt-4 flex flex-col gap-7 rounded-card bg-surface px-4 py-5 lg:flex-row lg:px-7 lg:py-6">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <h3 className="text-[15px] leading-[23px] font-bold text-ink">
              월별 추세
            </h3>
            <TrendLegend size="sm" />
          </div>
          <div className="mt-3.5">
            {trend.isPending ? (
              <Skeleton className="h-[180px] w-full" />
            ) : (
              <TrendChart
                year={year}
                points={points}
                highlightYm={ym}
                height={180}
                barSize={24}
                tickSize={11.5}
              />
            )}
          </div>
          <div className="mt-4 h-px bg-[#f1f1f1]" />
          <div className="mt-3.5 flex flex-col gap-3 sm:flex-row">
            <StatBox title="직전 5개월 평균">
              <StatRow
                label="평균 예상 금액"
                amount={t?.prescription.previous5MonthsAverage}
                delta={t?.prescription.previous5MonthsChangeRate}
              />
              <StatRow
                label="평균 확정 금액"
                amount={t?.settlement.previous5MonthsAverage}
                delta={t?.settlement.previous5MonthsChangeRate}
              />
            </StatBox>
            <StatBox title="전 월 대비">
              <StatRow
                label="예상 금액"
                amount={t?.prescription.previousMonthAmount}
                delta={t?.prescription.previousMonthChangeRate}
              />
              <StatRow
                label="확정 금액"
                amount={t?.settlement.previousMonthAmount}
                delta={t?.settlement.previousMonthChangeRate}
              />
            </StatBox>
          </div>
        </div>
        <div className="flex w-full flex-col lg:w-[412px] lg:shrink-0">
          <h3 className="text-[15px] leading-[23px] font-bold text-ink">
            {year}년 {monthOf(ym)}월 금액
          </h3>
          <div className="mt-3.5 flex flex-col gap-3">
            <AmountBox
              kind="prescription"
              target={m?.targetAmountPrescription}
              total={m?.estimatedAmountPrescription}
            />
            <AmountBox
              kind="settlement"
              target={m?.targetAmountSettlement}
              total={m?.hasSettlement ? m.confirmedAmountSettlement : null}
              ratio={m?.hasSettlement ? m.settlementRatio : null}
            />
          </div>
        </div>
      </section>
    </>
  );
};
